import { Gallery, GalleryPrivacy } from "./Gallery"
import Theme from "./Theme"
import { Button, ButtonBehavior } from "./Button"
import { PresetJson, PresetImage } from "./Preset"
import React, { ReactNode } from "react"
import { DataCapture } from "./DataCapture"
import { AiEffectSequenceResource } from "./models/AiEffectSequenceResource"
import { AspectRatio } from "../utils/AspectRatioUtil"

enum EditTool {
  BACKGROUNDS = "backgrounds",
  OVERLAYS = "overlays",
  STICKERS = "stickers",
  AI_EFFECT = "ai_effect",
}

export default class Config {
  theme: Theme
  gallery: Gallery
  presetId: number
  presetCode: string
  closed: boolean
  showGalleryOptIn: boolean
  ageGate: {
    enabled: boolean
    minimumAge: number
    prompt: string
  }
  headshots: {
    showFaceGuide: boolean
    showTips: boolean
    minimumSize: {
      width: number
      height: number
    }
  }
  dataCapture: DataCapture
  aspectRatio: AspectRatio
  isDev: boolean

  finishedPicMessage: string | null
  hideAttribution: boolean

  editTools: EditTool[]
  stickers: PresetImage[]
  backgrounds: PresetImage[]
  overlays: PresetImage[]

  backgroundReplacementOn: boolean
  backgroundReplacementRequired: boolean

  aiEffectOn: boolean
  aiEffectSequence: AiEffectSequenceResource | null

  buttons: {
    splashJoin: Button | null
    share: Button | null
    postShare: Button | null
    viewPic: Button | null
  }

  constructor(preset: PresetJson) {
    this.theme = new Theme(preset)
    this.gallery = new Gallery(preset._embedded.gallery)
    this.presetId = preset.id
    this.presetCode = preset.code
    this.showGalleryOptIn =
      this.gallery.privacy === GalleryPrivacy.PUBLIC ||
      this.gallery.privacy === GalleryPrivacy.UNLISTED
    this.ageGate = preset._embedded.ageGate ?? { enabled: false, prompt: "", age: 0 }

    this.dataCapture = {
      emailOn: preset._embedded.participantData.emailAddressCaptureEnabled,
      firstNameOn: preset._embedded.participantData.firstNameCaptureEnabled,
      lastNameOn: preset._embedded.participantData.lastNameCaptureEnabled,
      phoneNumberOn: preset._embedded.participantData.phoneNumberCaptureEnabled,
    }

    // TODO: This will later come from the preset
    this.aspectRatio = "1:1"

    this.isDev = Boolean(process.env.REACT_APP_IS_DEV === "true")

    this.finishedPicMessage = null

    this.hideAttribution = preset.hideAttribution

    // Default headshot settings
    this.headshots = {
      showFaceGuide: false,
      showTips: false,
      minimumSize: {
        width: 0,
        height: 0,
      },
    }

    // Set edit tools
    this.backgrounds = this.bustChromeCorsIssue(preset._embedded.backgroundReplacements)
    this.stickers = this.bustChromeCorsIssue(preset._embedded.stickers)
    this.overlays = this.bustChromeCorsIssue(preset._embedded.overlays)

    this.editTools = []
    if (this.backgrounds && this.backgrounds.length > 1) {
      this.editTools.push(EditTool.BACKGROUNDS)
    }
    this.backgroundReplacementOn = this.backgrounds.length > 0
    this.backgroundReplacementRequired = preset.backgroundReplacementRequired

    if (this.overlays && this.overlays.length > 1) {
      this.editTools.push(EditTool.OVERLAYS)
    }
    if (this.stickers && this.stickers.length > 0) {
      this.editTools.push(EditTool.STICKERS)
    }

    // AI effect sequence
    this.aiEffectSequence = preset._embedded.aiEffectSequence ?? null
    if (this.aiEffectSequence) {
      this.aiEffectOn = true
      this.backgroundReplacementOn = false
      this.editTools = this.editTools.filter((t) => t !== EditTool.BACKGROUNDS)
      this.editTools.unshift(EditTool.AI_EFFECT)
    } else {
      this.aiEffectOn = false
    }

    // Close booth if uploading is disabled, or if AI features are active but no credits remain
    const usesAiFeatures = this.aiEffectOn || this.backgroundReplacementOn
    this.closed = !preset.uploadingEnabled || (usesAiFeatures && !preset.hasAvailableCredits)

    // Set buttons
    this.buttons = {
      splashJoin: null,
      share: null,
      postShare: null,
      viewPic: null,
    }
  }

  // Resolves a CORS issue in Chrome
  // Details: https://www.hacksoft.io/blog/handle-images-cors-error-in-chrome
  bustChromeCorsIssue(images: PresetImage[]) {
    images.forEach((image) => {
      image.url += "?blameChrome"
    })
    return images
  }
}
